import React from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface EmailStepProps {
  email: string;
  validation: { valid: boolean; error: string };
  onChange: (field: string, value: string) => void;
  onSubmit: () => void;
  isMobile: boolean;
}

const EmailStep: React.FC<EmailStepProps> = ({
  email,
  validation,
  onChange,
  onSubmit,
  isMobile,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (email.trim() !== "" && validation.valid) {
        onSubmit();
      }
    }
  };

  const isValid = validation.valid && email.trim() !== "";
  const hasError = !validation.valid && email.length > 0;

  return (
    <Box>
      <Typography
        variant="subtitle2"
        align="center"
        sx={{
          color: "#9ca3af",
          fontWeight: 500,
          mb: 2,
          fontSize: "0.875rem",
        }}
      >
        {t("form.sixthQuestion.title")}
      </Typography>

      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#1e293b",
          fontSize: { xs: "1.2rem", md: "1.5rem" },
        }}
      >
        8. {t("form.eighthQuestion.question")}
        <span style={{ color: "#ef4444" }}>*</span>
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <EmailIcon sx={{ color: "#64748b", fontSize: 28 }} />
          <Typography
            variant="body2"
            sx={{
              color: "#64748b",
              fontWeight: 500,
            }}
          >
            Your email address
          </Typography>
        </Box>

        <TextField
          fullWidth
          type="email"
          placeholder={t("form.eighthQuestion.placeholder")}
          value={email}
          onChange={(e) => {
            const newValue = e.target.value;
            onChange("email", newValue);
          }}
          onKeyDown={handleKeyDown}
          error={hasError}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "#f8fafc",
              fontSize: "1.125rem",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
              "&.Mui-focused": {
                backgroundColor: "#ffffff",
                "& fieldset": {
                  borderColor: isValid ? "#22c55e" : "#3b82f6",
                  borderWidth: "2px",
                },
              },
              "&.Mui-error": {
                "& fieldset": {
                  borderColor: "#ef4444",
                  borderWidth: "2px",
                },
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "16px 14px",
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 1.5,
            px: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {hasError && (
              <>
                <ErrorOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <Typography variant="caption" color="error">
                  {validation.error}
                </Typography>
              </>
            )}
            {isValid && (
              <>
                <CheckCircleIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                <Typography
                  variant="caption"
                  sx={{ color: "#22c55e", fontWeight: 500 }}
                >
                  Valid email address!
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          backgroundColor: "#eff6ff",
          borderRadius: "8px",
          border: "1px solid #bfdbfe",
          mb: 3,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#1e40af",
            fontSize: "0.875rem",
            lineHeight: 1.6,
          }}
        >
          🔒 <strong>Privacy:</strong> We'll use this email to send you case
          updates and important information. We never share your email with
          third parties.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={email.trim() === "" || !validation.valid}
          sx={{
            minWidth: "200px",
            py: 1.5,
            px: 4,
            fontWeight: 600,
            fontSize: "1rem",
            borderRadius: "10px",
            backgroundColor: "#3b82f6",
            textTransform: "none",
            boxShadow: isValid
              ? "0 4px 14px 0 rgba(59, 130, 246, 0.39)"
              : "none",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#2563eb",
              boxShadow: "0 6px 20px rgba(59, 130, 246, 0.5)",
              transform: "translateY(-1px)",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              color: "#94a3b8",
              boxShadow: "none",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default EmailStep;
